import { initialRouterState } from 'connected-next-router';
import Router from 'next/router';
import { NextSeo, type NextSeoProps } from 'next-seo';
import { type ReactNode, useMemo } from 'react';
import { Provider as ReduxProvider } from 'react-redux';

import { type ShortUrlForm } from '@air/api/types';
import { ContentCollectionView } from '@air/feature-content-collection/redux';
import { MediaQueryProvider } from '@air/provider-media-query';
import { ModalProvider } from '@air/provider-modal';
import { ShortIdProvider } from '@air/provider-short-id';
import { ErrorBoundary } from '@air/utils-error';

import { ContentCollectionSkeleton } from '~/components/ContentCollection/ContentCollectionSkeleton';
import { ContentCollectionCustomFieldsProvider } from '~/components/ContentCollection/providers/ContentCollectionCustomFieldsProvider';
import { MaintenanceMode } from '~/components/MaintenanceMode/MaintenanceMode';
import { PublicBoardSocketContextProvider } from '~/components/PublicBoardSocketContextProvider';
import { PublicWorkspaceProvider } from '~/components/PublicWorkspaceProvider';
import { PublicAccountProvider } from '~/providers/AccountProvider';
import { ExternalLinksClickableProvider } from '~/providers/ExternalLinksClickableProvider';
import { ModifiersKeysPressedProvider } from '~/providers/ModifiersKeysPressedProvider';
import { ReCaptchaProvider } from '~/providers/ReCaptchaProvider';
import { makeStore } from '~/store';

export type ContentCollectionLayoutProps = {
  children: ReactNode;
  data: ShortUrlForm;
  seo: NextSeoProps;
  shortId: string;
  isLoading?: boolean;
  isOnPaidPlan: boolean | undefined;
};

export const ContentCollectionLayout = ({
  children,
  data,
  seo,
  shortId,
  isLoading,
  isOnPaidPlan,
}: ContentCollectionLayoutProps) => {
  const { store } = useMemo(() => {
    return makeStore({
      router: initialRouterState(typeof window === 'undefined' ? undefined : Router.asPath),
      contentCollection: {
        applyAll: true,
        customFields: [],
        form: data,
        touched: false,
        attemptedSubmission: false,
        clips: {},
        submittedClips: [],
        view: ContentCollectionView.Onboarding,
      },
    });
  }, [data]);

  const workspace = useMemo(
    () => ({
      id: data.workspaceId,
      name: data.workspace.name,
    }),
    [data.workspaceId, data.workspace.name],
  );

  return (
    <ReCaptchaProvider siteKey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}>
      <MaintenanceMode>
        <ExternalLinksClickableProvider areExternalLinksClickable={isOnPaidPlan === true}>
          <ShortIdProvider shortId={shortId} objectId={data.boardId} objectType="board" isOnPaidPlan={isOnPaidPlan}>
            <ReduxProvider store={store}>
              <ErrorBoundary>
                <PublicWorkspaceProvider
                  workspaceId={data.workspaceId}
                  workspaceName={data.workspace.name}
                  workspaceImage={data.workspace.image}
                >
                  <PublicBoardSocketContextProvider>
                    <MediaQueryProvider>
                      <PublicAccountProvider workspace={workspace}>
                        <ContentCollectionCustomFieldsProvider>
                          <ModifiersKeysPressedProvider>
                            <ModalProvider>
                              <NextSeo {...seo} />
                              {isLoading ? <ContentCollectionSkeleton /> : children}
                            </ModalProvider>
                          </ModifiersKeysPressedProvider>
                        </ContentCollectionCustomFieldsProvider>
                      </PublicAccountProvider>
                    </MediaQueryProvider>
                  </PublicBoardSocketContextProvider>
                </PublicWorkspaceProvider>
              </ErrorBoundary>
            </ReduxProvider>
          </ShortIdProvider>
        </ExternalLinksClickableProvider>
      </MaintenanceMode>
    </ReCaptchaProvider>
  );
};
